import { Add } from "@mui/icons-material"
import { Box, IconButton } from "@mui/material"
import log from "loglevel"
import { useSnackbarStore } from "../../components/Snackbar/Snackbar"
import { FlightlineEventReview, ReviewDistanceInfo } from "../../models/MissionReview"
import {
    DoubleReviewSplit,
    SingleReviewSplit,
    addNewDistReviewFromNextAndPrevCard,
    addNewDistReviewFromNextCard,
    addNewDistReviewFromPrevCard,
} from "../../utils/DistanceSplitUtils"
import { ReviewType } from "../../utils/MissionReviewUtils"
import {
    addNewNodeReviewFromNext,
    addNewNodeReviewFromNextAndPrev,
    addNewNodeReviewFromPrev,
} from "../../utils/NodeSplitUtils"
import { useMissionReviewStore } from "../../zstore/MissionReviewStore"

export const AddNewShapeSplitCard = ({ index, reviewType }: { index: number; reviewType: ReviewType }) => {
    const alert = useSnackbarStore((state) => state.handleOpen)

    const allReviews = useMissionReviewStore((state) => state.flightLineReviews) ?? []
    const selectedReviews = useMissionReviewStore((state) => state.currentPartialLines) ?? []
    const updatePartialLines = useMissionReviewStore((state) => state.updatePartialLines) ?? []

    const nextReview = index === selectedReviews.length ? undefined : selectedReviews[index]
    const prevReview = index === 0 ? undefined : selectedReviews[index - 1]

    let simpleSplitMinimum: number
    let lastDitchSplitMinium: number
    let totalPreviousReviewQuantity: number
    let totalNextReviewQuantity: number
    let enoughToSplit: boolean
    let addNewReviewFromNext: (nextReview: FlightlineEventReview, newFeReviewId: number) => SingleReviewSplit
    let addNewReviewFromPrevious: (nextReview: FlightlineEventReview, newFeReviewId: number) => SingleReviewSplit
    let addNewReviewFromNextAndPrevCard: (
        prevReview: FlightlineEventReview,
        nextReview: FlightlineEventReview,
        feReviewId: number,
    ) => DoubleReviewSplit
    let errorMessage: string

    switch (reviewType) {
        case ReviewType.NODE:
            simpleSplitMinimum = 4
            lastDitchSplitMinium = 3
            totalPreviousReviewQuantity = prevReview?.node_info?.node_count ?? 0
            totalNextReviewQuantity = nextReview?.node_info?.node_count ?? 0

            /*
                A new card requires new first and last nodes from a neighboring review (4 nodes required in total - each needing at least 2)
                - at first we will use the next review card to separate out two nodes
                - if there is no next card then the nodes will be from the previous review
                - if there is a next card and previous card but both only have 3 nodes - then we can take one node from each
             */
            enoughToSplit =
                totalPreviousReviewQuantity +
                    totalNextReviewQuantity -
                    (nextReview && prevReview ? 6 : simpleSplitMinimum) >=
                0

            addNewReviewFromNext = addNewNodeReviewFromNext
            addNewReviewFromPrevious = addNewNodeReviewFromPrev
            addNewReviewFromNextAndPrevCard = addNewNodeReviewFromNextAndPrev
            errorMessage = "Unable to add new review. Calculation of available nodes to take from is invalid"
            break
        case ReviewType.DISTANCE:
            simpleSplitMinimum = 0.02
            lastDitchSplitMinium = 0.03
            totalPreviousReviewQuantity = getTotalNMAvailable(prevReview?.distance_info)
            totalNextReviewQuantity = getTotalNMAvailable(nextReview?.distance_info)

            /**
             * Requirements when splitting:
             * - need to have no gaps in lines. So one review can be 1nm - 5nm and the next 5nm - 6nm
             * - if only one neighboring review is available we only need 0.3 nm in total
             *      start 0.01 - stop 0.02 --- start 0.02 stop 0.03
             * - if next and previous reviews have 0.03 in total then make a new review in between
             *     start 0 -> ( 0.01 - 0.02 ) -> 0.03  end
             */
            enoughToSplit =
                totalPreviousReviewQuantity +
                    totalNextReviewQuantity -
                    (prevReview && nextReview ? lastDitchSplitMinium : simpleSplitMinimum) >=
                0

            addNewReviewFromNext = addNewDistReviewFromNextCard
            addNewReviewFromPrevious = addNewDistReviewFromPrevCard
            addNewReviewFromNextAndPrevCard = addNewDistReviewFromNextAndPrevCard
            errorMessage = "Unable to add new review. Calculation of available Nautical Miles to take from is invalid"
            break
    }

    const handleAddNewCard = () => {
        // We need to calculate a fe_review_id between all the reviews and
        // the other new reviews that aren't saved in the table yet
        const totalExistingNewReviews = selectedReviews.filter((eventReview) => !eventReview.id)
        const newReviewId = allReviews.concat(totalExistingNewReviews).length + 1

        let updatedReviews: FlightlineEventReview[] = []
        if (nextReview && totalNextReviewQuantity >= simpleSplitMinimum) {
            const { updatedReview, newReview } = addNewReviewFromNext(nextReview, newReviewId)

            log.info(`add new review using NEXT card at ${index} index`)
            updatedReviews = [
                ...selectedReviews.slice(0, index),
                newReview,
                updatedReview,
                ...selectedReviews.slice(index + 1),
            ]
        } else if (prevReview && totalPreviousReviewQuantity >= simpleSplitMinimum) {
            const { updatedReview, newReview } = addNewReviewFromPrevious(prevReview, newReviewId)

            log.info(`add new review using PREVIOUS card at ${index - 1} index`)
            updatedReviews = [
                ...(index === 0 ? [] : selectedReviews.slice(0, index - 1)),
                updatedReview,
                newReview,
                ...selectedReviews.slice(index),
            ]
        } else if (
            prevReview &&
            nextReview &&
            // Since the reviews overlap we want the total of the prev and next review
            totalPreviousReviewQuantity + totalNextReviewQuantity === lastDitchSplitMinium
        ) {
            // Last ditch effort

            const { updatedPreviousReview, updatedNextReview, newReview } = addNewReviewFromNextAndPrevCard(
                prevReview,
                nextReview,
                newReviewId,
            )

            log.info(`add new review using both cards at index ${index - 1} and ${index}`)
            updatedReviews = [
                ...(index === 0 ? [] : selectedReviews.slice(0, index - 1)),
                updatedPreviousReview,
                newReview,
                updatedNextReview,
                ...selectedReviews.slice(index + 1),
            ]
        } else {
            log.error(errorMessage)
            alert({ message: errorMessage, severity: "error" })
            return
        }

        const segmentedReviews = updatedReviews.map(
            (review, index): FlightlineEventReview => ({
                ...review,
                segment_number: index + 1,
                is_final_segment: index === updatedReviews.length - 1,
            }),
        )
        updatePartialLines(segmentedReviews)
    }

    return (
        <Box sx={{ width: 1, display: "flex", justifyContent: "center" }}>
            <IconButton color="primary" onClick={handleAddNewCard} disabled={!enoughToSplit} tabIndex={-1}>
                <Add />
            </IconButton>
        </Box>
    )
}

const getTotalNMAvailable = (distanceInfo?: ReviewDistanceInfo) => {
    if (distanceInfo) {
        return distanceInfo.stop_distance_nm - distanceInfo.start_distance_nm
    }
    return 0
}
